import {CommonEventType, makeGroupEventsOffline} from "@src/Store/eventsStore.ts";
import {useMemo} from "react";
import {WorkingTime} from "@src/Pages/Views/Common.tsx";
import styles from "./dailyEvents.module.less";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import cn from "classnames";
import {Gt} from "@src/i18n/Gt.tsx";
import {HeaderItem, TableHeaderAction} from "@src/Pages/Views/tableTypes.ts";
import {EmployeeWithWorkdays, WorkDayWithEvents} from "@src/Store/employeesStore.ts";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {
    Body_10_Coral_Primary_Regular10,
    Body_14_Coral_Primary_Regular_14,
} from "@UI/typography/generated/typography.tsx";
import {getLocationLink} from "@src/utils/coordLink.ts";
import {getTimeFromDT} from "@src/utils/formatDate.ts";
import {DailyWorkdaysTable} from "@src/Pages/Views/Manager/AttendanceHistory/DailyEvents/EventsTable/EventsTable.tsx";
import {CircleWarning} from "@UI/components/icons/CircleWarning.tsx";
import {colorsMap, EmployeeStatus, workdayStatus} from "@src/CommonComponents/WorkStatus/WorkStatus.tsx";
import {EventsHistoryGroup} from "@src/CommonComponents/EventsHistoryElement/EventsHistoryGroup.tsx";
import {Colors} from "@UI/theme/generated/Colors.ts";

export const WorkStatus = ({workday}: { workday?: WorkDayWithEvents }) => {
    const status = workdayStatus(workday)
    return (
        <Stack className={styles.workStatus} style={{backgroundColor: colorsMap[status]}}
               alignItems={"center"} justifyContent={"center"}>
            {status === EmployeeStatus.wrongData && <CircleWarning size={18}/>}
            <Body_10_Coral_Primary_Regular10>
                <Gt prefix={"employee_statuses"}>{status}</Gt>
            </Body_10_Coral_Primary_Regular10>
        </Stack>

    )
}

export const renderEmployeeStatus = (workdays: WorkDayWithEvents[]) => {
    return (
        <div style={{maxWidth: "80px"}}>
            <WorkStatus workday={workdays?.[0] ?? []}/>
        </div>
    )
}

export const LocationLink = ({event}: { event: CommonEventType }) => {
    const value = useMemo(() => {
        if (!event?.latitude || !event?.longitude) return undefined
        if (event?.branch_name) return (
            <Stack direction={"column"} alignItems={"flex-start"}>
                <Body_14_Coral_Primary_Regular_14>
                    {event.branch_name}
                </Body_14_Coral_Primary_Regular_14>
            </Stack>
        )
        return (
            <Stack direction={"column"} alignItems={"flex-start"}>
                <Body_14_Coral_Primary_Regular_14>
                    <Gt>latitude</Gt>: {event?.latitude?.toFixed(4)}
                </Body_14_Coral_Primary_Regular_14>
                <Body_14_Coral_Primary_Regular_14>
                    <Gt>longitude</Gt>: {event?.longitude?.toFixed(4)}
                </Body_14_Coral_Primary_Regular_14>
            </Stack>
        )
    }, [event])

    const link = useMemo(() => {
        return getLocationLink(event)
    }, [event])

    if (!value || !event) return (
        <Body_14_Coral_Primary_Regular_14>
            —:—
        </Body_14_Coral_Primary_Regular_14>
    )

    return (
        <Button type={ButtonType.link}
                className={styles.coordsButton}
                color={ColorNames.CORAL_INK}
                externalLink={true}
                href={link}>
            {value}
        </Button>
    )
}


export const WorkCoords = ({workday, type}: { workday: WorkDayWithEvents, type: "in" | "out" }) => {
    const events = useMemo(() => {
        if (!workday) return []
        return makeGroupEventsOffline(workday?.events ?? [])
    }, [workday])

    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —:—
        </Body_14_Coral_Primary_Regular_14>
    )
    if (!events || events?.length === 0) return noData
    let event = type === "in" ? events[0]?.event_in : events[events.length - 1]?.event_out;
    if (!event) return noData
    return <LocationLink event={event}/>
}

export const renderCoords = (workday: WorkDayWithEvents, type: "in" | "out") => {
    return <WorkCoords workday={workday} type={type}/>
}

export const WorkTime = ({workday, type}: { workday: WorkDayWithEvents, type: "in" | "out" }) => {
    const events = useMemo(() => {
        if (!workday) return []
        return makeGroupEventsOffline(workday?.events ?? [])
    }, [workday])

    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —
        </Body_14_Coral_Primary_Regular_14>
    )
    if (!events || events?.length === 0) return noData
    const event = type === "in" ? events[0]?.event_in : events[events.length - 1]?.event_out;
    if (!event) return noData
    return (
        <Body_14_Coral_Primary_Regular_14>
            {getTimeFromDT(event?.recorded_at)}
        </Body_14_Coral_Primary_Regular_14>
    )
}

export const renderTime = (workday: WorkDayWithEvents, type: "in" | "out") => {
    return <WorkTime workday={workday} type={type}/>
}

export const headerItems: Array<HeaderItem> = [
    {
        label: <Gt>Employee name</Gt>,
        dataLabel: "name",
        paramKey: 'name',
        paramKeys: ['last_name', 'first_name'],
        type: TableHeaderAction.sorting
    },
    {
        label: <Gt>Activity status</Gt>,
        dataLabel: "activity_status",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderEmployeeStatus(data)
    },
    {
        label: <Gt>Work time</Gt>,
        dataLabel: "checkin_time",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderTime(data?.[0], "in")
    },
    {
        label: <Gt>GPS in</Gt>,
        dataLabel: "checkin_coords",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderCoords(data?.[0], "in")
    },
    {
        label: <Gt>Leave work time</Gt>,
        dataLabel: "checkout_time",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderTime(data?.[0], "out")
    },
    {
        label: <Gt>GPS out</Gt>,
        dataLabel: "checkout_coords",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderCoords(data?.[0], "out")
    },
    {
        label: <Gt>Total working hours</Gt>,
        dataLabel: "working_time",
        paramKey: 'workdays',
        type: TableHeaderAction.sorting,
        renderer: (workdays) => <WorkingTime workdays={workdays}/>
    },
]


const EmployeeDailyEvents = ({employee}: { employee: EmployeeWithWorkdays }) => {
    const {workday, eventsGroup} = useMemo(() => {
        if (!employee) return {
            workday: undefined,
            eventsGroup: undefined,
        }
        //@ts-ignore
        const eventsGroup = makeGroupEventsOffline(employee?.workdays?.[0]?.events ?? [], employee)
        return {
            workday: employee?.workdays?.[0],
            eventsGroup
        }
    }, [employee])

    return (
        <Stack direction={"column"} className={cn(styles.whiteBoard, styles.eventsBoard)}
               key={employee?.id}>
            <EventsHistoryGroup
                footerBgColor={Colors.Obsidian_Light_0}
                employee={employee}
                workDay={workday}
                eventsGroup={eventsGroup}
            />
        </Stack>
    )
}


export const DailyEvents = ({employees}: { employees?: EmployeeWithWorkdays[] }) => {
    if (!employees) return null
    return (
        <>
            <div className={styles.eventsGroups}>
                {employees?.map(employee =>
                    <EmployeeDailyEvents employee={employee} key={employee?.id}/>
                )}
            </div>
            <DailyWorkdaysTable headerColumns={headerItems} content={employees ?? []}/>
        </>
    )
}
